import { useState, useCallback } from 'react';
import { updateVehicle } from '../api/vehicleService';
import type { Vehicle, Reservation } from '../types';

interface ToastState {
  open: boolean;
  message: string;
  severity: string;
}

interface UnsavedConfirmState {
  targetVehicle?: Vehicle | null;
  targetReservation?: Reservation | null;
  closeVehiclePanel?: boolean;
}

interface UseVehiclePanelOptions {
  user: { id: string | number; [key: string]: any } | null;
  setVehicles: (updater: (prev: Record<string, Vehicle>) => Record<string, Vehicle>) => void;
  onToast: (toastState: ToastState) => void;
}

/**
 * useVehiclePanel
 *
 * 車両詳細パネルの選択・未保存確認・保存ロジックを管理するフック。
 * DayListReservations から抽出。
 */
export function useVehiclePanel({ user, setVehicles, onToast }: UseVehiclePanelOptions) {
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [selectedVehicleReservation, setSelectedVehicleReservation] = useState<Reservation | null>(null);
  const [vehiclePanelDirty, setVehiclePanelDirty] = useState<boolean>(false);
  const [unsavedConfirm, setUnsavedConfirm] = useState<UnsavedConfirmState | null>(null);

  const handleBeforeOpenReservation = useCallback((): boolean => {
    if (selectedVehicle) {
      if (vehiclePanelDirty) {
        setUnsavedConfirm({ closeVehiclePanel: true });
        return false;
      }
      setSelectedVehicle(null);
      setSelectedVehicleReservation(null);
      setVehiclePanelDirty(false);
    }
    return true;
  }, [selectedVehicle, vehiclePanelDirty]);

  const handleVehicleClick = useCallback((vehicle: Vehicle, reservation?: Reservation | null) => {
    if (selectedVehicle && vehiclePanelDirty) {
      setUnsavedConfirm({ targetVehicle: vehicle, targetReservation: reservation });
      return;
    }
    setSelectedVehicle(vehicle);
    setSelectedVehicleReservation(reservation ?? null);
    setVehiclePanelDirty(false);
  }, [selectedVehicle, vehiclePanelDirty]);

  const handleClosePanel = useCallback(() => {
    if (vehiclePanelDirty) {
      setUnsavedConfirm({ targetVehicle: null });
      return;
    }
    setSelectedVehicle(null);
    setSelectedVehicleReservation(null);
    setVehiclePanelDirty(false);
  }, [vehiclePanelDirty]);

  const handleUnsavedConfirmAction = useCallback((action: string) => {
    if (action === 'cancel') {
      setUnsavedConfirm(null);
      return;
    }
    if (action === 'discard') {
      const { targetVehicle, targetReservation, closeVehiclePanel } = unsavedConfirm || {};
      setUnsavedConfirm(null);
      setVehiclePanelDirty(false);

      if (closeVehiclePanel) {
        setSelectedVehicle(null);
        setSelectedVehicleReservation(null);
      } else {
        setSelectedVehicle(targetVehicle ?? null);
        setSelectedVehicleReservation(targetReservation ?? null);
      }
    }
  }, [unsavedConfirm]);

  const handleSaveVehicle = useCallback(async (updatedVehicle: any) => {
    try {
      const result = await updateVehicle(updatedVehicle, user?.id);
      if (result) {
        onToast({ open: true, message: '車両情報を更新しました', severity: 'success' });
        setVehicles(prev => ({ ...prev, [result.id]: result }));
        setSelectedVehicle(result);
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      onToast({ open: true, message: '更新に失敗しました', severity: 'error' });
    }
  }, [user, setVehicles, onToast]);

  return {
    selectedVehicle,
    setSelectedVehicle,
    selectedVehicleReservation,
    setSelectedVehicleReservation,
    vehiclePanelDirty,
    setVehiclePanelDirty,
    unsavedConfirm,
    handleBeforeOpenReservation,
    handleVehicleClick,
    handleClosePanel,
    handleUnsavedConfirmAction,
    handleSaveVehicle,
  };
}
